import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import {
  Globe,
  ShoppingBag,
  Package,
  TrendingUp,
  Plus,
  ArrowRight,
  Sparkles,
  Zap,
  BarChart3
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, planLimits, getPlanName } = useSubscription();
  const [stats, setStats] = useState({
    websites: 0,
    products: 0,
    orders: 0,
    revenue: 0
  });
  const [recentWebsites, setRecentWebsites] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch websites count
      const websitesQuery = query(
        collection(db, 'websites'),
        where('userId', '==', user.uid)
      );
      const websitesSnap = await getDocs(websitesQuery);
      const websitesCount = websitesSnap.size;
      
      // Fetch recent websites
      const recentWebsitesQuery = query(
        collection(db, 'websites'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc'),
        limit(3)
      );
      const recentWebsitesSnap = await getDocs(recentWebsitesQuery);
      const websitesList = recentWebsitesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Fetch products count
      const productsQuery = query(
        collection(db, 'products'),
        where('userId', '==', user.uid)
      );
      const productsSnap = await getDocs(productsQuery);
      const productsCount = productsSnap.size;
      
      // Fetch orders
      const ordersQuery = query(
        collection(db, 'orders'),
        where('userId', '==', user.uid)
      );
      const ordersSnap = await getDocs(ordersQuery);
      const ordersCount = ordersSnap.size;
      
      // Calculate revenue
      let totalRevenue = 0;
      const ordersList = [];
      ordersSnap.docs.forEach(doc => {
        const order = doc.data();
        totalRevenue += order.total || 0;
        ordersList.push({ id: doc.id, ...order });
      });
      
      setStats({
        websites: websitesCount,
        products: productsCount,
        orders: ordersCount,
        revenue: totalRevenue
      });
      
      setRecentWebsites(websitesList);
      setRecentOrders(ordersList.slice(0, 3));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Websites',
      value: stats.websites,
      limit: planLimits?.websites || 1,
      icon: Globe,
      color: 'blue',
      path: '/site/websites'
    },
    {
      title: 'Products',
      value: stats.products,
      limit: planLimits?.products || 10,
      icon: ShoppingBag,
      color: 'purple',
      path: '/site/products'
    },
    {
      title: 'Orders',
      value: stats.orders,
      icon: Package,
      color: 'green',
      path: '/site/orders'
    },
    {
      title: 'Revenue',
      value: `$${stats.revenue.toFixed(2)}`,
      icon: TrendingUp,
      color: 'orange',
      path: '/site/analytics'
    }
  ];

  const getColorClass = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      green: 'from-green-500 to-green-600',
      orange: 'from-orange-500 to-orange-600'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-6 lg:p-8 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">
              Welcome back, {user?.displayName?.split(' ')[0] || 'User'}!
            </h1>
            <p className="text-primary-100">
              You're on the <span className="font-semibold">{getPlanName()}</span> plan. 
              {subscription?.plan === 'free' && ' Upgrade to unlock more features!'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/site/wizard')}
              className="flex items-center gap-2 px-4 py-2 bg-white text-primary-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Website
            </button>
            <button
              onClick={() => navigate('/plans')}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors"
            >
              <Sparkles className="w-5 h-5" />
              Upgrade
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            onClick={() => navigate(stat.path)}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                {stat.limit && (
                  <p className="text-xs text-gray-400 mt-1">
                    of {stat.limit} limit
                  </p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getColorClass(stat.color)} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Websites */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Websites</h2>
            <button
              onClick={() => navigate('/site/websites')}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          
          {recentWebsites.length > 0 ? (
            <div className="space-y-3">
              {recentWebsites.map((website) => (
                <div
                  key={website.id}
                  onClick={() => navigate(`/site/editor/${website.id}`)}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-purple-500 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{website.name}</h3>
                    <p className="text-sm text-gray-500">{website.domain || 'No domain'}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    website.status === 'published'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {website.status || 'draft'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No websites yet</p>
              <button
                onClick={() => navigate('/site/wizard')}
                className="mt-3 text-primary-600 hover:text-primary-700 font-medium"
              >
                Create your first website
              </button>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/site/wizard')}
              className="w-full flex items-center gap-3 p-4 bg-primary-50 rounded-xl hover:bg-primary-100 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Create Website</p>
                <p className="text-sm text-gray-500">Start building a new site</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/site/products')}
              className="w-full flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Add Product</p>
                <p className="text-sm text-gray-500">Add a new product</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/site/analytics')}
              className="w-full flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-gray-900">View Analytics</p>
                <p className="text-sm text-gray-500">Check your stats</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
